// src/components/pages/Foam/charts/PressureChart.tsx
import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  Legend,
  Line,
  Label,
  ReferenceLine,
} from "recharts";
import { Box } from "@mui/material";
import {
  getClickedTMs,
  renderMarkers,
  ChartBandZoomButton,
  axisTickFormatter,
  fmtDateTime,
} from "../../../common/chartZoom";

type Props = {
  data: any[];
  domain: [number, number] | null;
  markers?: number[];
  onChartClick?: (t: number) => void;
  onZoom?: () => void;
  /** High-pressure warning setpoint (VD74), drawn as a dashed line. */
  warnAt?: number | null;
  /** High-pressure emergency stop setpoint (VD78), drawn as a dashed line. */
  shutdownAt?: number | null;
};

const PressureChart: React.FC<Props> = ({
  data,
  domain,
  markers = [],
  onChartClick,
  onZoom,
  warnAt,
  shutdownAt,
}) => {
  const handleClick = (state: any) => {
    if (!onChartClick) return;
    const t = getClickedTMs(state);
    if (t != null) onChartClick(t);
  };

  return (
    <Box sx={{ height: 340, position: "relative" }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ left: 60, right: 8, top: 10, bottom: 8 }}
          onClick={handleClick}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="tMs"
            domain={domain ?? (["auto", "auto"] as any)}
            tickFormatter={axisTickFormatter(domain)}
            minTickGap={20}
          />
          {/* Headroom above the emergency stop line: at a flat 4000 the 4200 setpoint
              sat off the top, so a pressure heading for shutdown was clipped. */}
          <YAxis
            domain={[0, Math.max(4000, Math.ceil(((shutdownAt ?? 0) + 300) / 500) * 500)]}
            tickMargin={8}
            width={56}
          >
            <Label
              value="(PSI)"
              angle={0}
              position="left"
              offset={25}
              style={{ textAnchor: "middle" }}
            />
          </YAxis>
          <RTooltip labelFormatter={(t) => fmtDateTime(Number(t))} />
          <Legend />
          <Line
            type="monotone"
            dataKey="lp"
            name="Low"
            stroke="#0288d1"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="hp1"
            name="High 1"
            stroke="#6a1b9a"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="hp2"
            name="High 2"
            stroke="#ff8f00"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          {warnAt != null && (
            <ReferenceLine
              y={warnAt}
              stroke="#ed6c02"
              strokeDasharray="6 4"
              label={{ value: `Warning ${warnAt}`, position: "insideTopRight", fill: "#ed6c02", fontSize: 12 }}
            />
          )}
          {shutdownAt != null && (
            <ReferenceLine
              y={shutdownAt}
              stroke="#d32f2f"
              strokeDasharray="6 4"
              label={{ value: `Emergency stop ${shutdownAt}`, position: "insideTopRight", fill: "#d32f2f", fontSize: 12 }}
            />
          )}
          {renderMarkers(markers)}
        </LineChart>
      </ResponsiveContainer>
      <ChartBandZoomButton domain={domain} markers={markers} onZoom={onZoom} />
    </Box>
  );
};

export default React.memo(PressureChart);
